import { useEffect, useState } from "react";
import { getToken } from "../../../services/cookies";
import {
  getUserProfile,
  postCheckIn,
  getCheckInCheck,
} from "../../../services/api";
import chatSocketService from "../../../services/websocket";

export const useChatAuth = (venueUUID, navigate) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkInError, setCheckInError] = useState(null);
  const [hasCheckedIn, setHasCheckedIn] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);

  const handleAuthError = (message) => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setCheckInError(message);
    setTimeout(() => navigate("/", { replace: true }), 3000);
  };

  const performCheckIn = async (venueId) => {
    try {
      setIsCheckingIn(true);
      setCheckInError(null);

      setHasCheckedIn(true);
      console.log("✅ hasCheckedIn set to true");
    } catch (error) {
      let errorMessage = "Mekana giriş yapılamadı";
      if (error.response?.status === 404) errorMessage = "Mekan bulunamadı";
      else if (error.response?.status === 403)
        errorMessage = "Bu mekana giriş yetkiniz yok";
      else if (error.response?.status === 400)
        errorMessage = "Geçersiz mekan kodu";

      console.error("❌ Check-in hatası:", error);
      handleAuthError(errorMessage);
      return;
    } finally {
      setIsCheckingIn(false);
    }
    
    // Check-in kontrol ve işlemi için ayrı try-catch bloku
    try {
      console.log("🔍 Check-in durumu kontrol ediliyor...");
      const checkInCheck = await getCheckInCheck(venueId);
      console.log("✅ Check-in kontrolü tamamlandı:", checkInCheck);
      
      if (checkInCheck === true) {
        console.log("✅ Kullanıcı zaten check-in yapmış");
      } else {
        console.log("📍 Check-in yapılıyor...");
        const checkInResponse = await postCheckIn(venueId);
        console.log("✅ Check-in başarılı:", checkInResponse);
      }
    } catch (checkInError) {
      console.error("❌ Check-in işlem hatası:", checkInError);
      
      // CORS hatası veya network hatası durumunda kullanıcıyı bilgilendir
      if (checkInError.message?.includes('CORS') || 
          checkInError.message?.includes('Network Error') ||
          checkInError.code === 'ERR_NETWORK') {
        console.warn("⚠️ CORS/Network hatası tespit edildi, check-in atlanıyor");
        // Check-in hatasını görmezden gel, chat'e devam et
      } else if (checkInError.response?.status === 401) {
        console.warn("⚠️ Yetkilendirme hatası, login'e yönlendir");
        handleAuthError("Oturum süreniz dolmuş, lütfen tekrar giriş yapın");
      } else {
        // Diğer check-in hatalarını da sessizce geç
        console.warn("⚠️ Check-in hatası görmezden geliniyor:", checkInError.message);
      }
    }
  };

  // Socket bağlantısını kur
  const connectToSocket = async () => {
    // Duplicate connection'ı önle
    if (socketConnected || isConnecting) {
      console.log("⚠️ Socket zaten bağlı veya bağlanıyor, duplicate önlendi");
      return;
    }

    try {
      setIsConnecting(true);
      console.log("🔌 Socket bağlantısı kuruluyor...");
      
      await chatSocketService.connect();
      setSocketConnected(true);
      console.log("✅ Socket başarıyla bağlandı");
      
      // Socket'ten kullanıcı bilgilerini al
      if (chatSocketService.user) {
        const socketUser = chatSocketService.user;
        const gender = socketUser.gender?.toLowerCase();
        let defaultAvatar =
          "https://st3.depositphotos.com/6672868/13701/v/450/depositphotos_137014128-stock-illustration-user-profile-icon.jpg";

        if (gender === "male") defaultAvatar = "/assets/qriendManAvatar.png";
        else if (gender === "female")
          defaultAvatar = "/assets/qriendWomenAvatar.png";

        console.log('🔥 Socket kullanıcı bilgisi:', socketUser);

        const userWithBackendId = {
          id: String(socketUser.id), // String'e çevir
          name: socketUser.name || socketUser.firstname || socketUser.email || "Kullanıcı",
          avatar: socketUser.avatar || socketUser.profilePicture || defaultAvatar,
          backendUserId: String(socketUser.backendUserId || socketUser.userId || socketUser.id),
        };

        console.log('👤 CurrentUser set edildi (socket):', userWithBackendId);
        setCurrentUser(userWithBackendId);
      } else {
        console.log('🔥 Socket kullanıcı bilgisi YOK, setTimeout ile bekleniyor...');
        // Socket henüz kullanıcı bilgisini almamış olabilir, biraz bekle
        setTimeout(() => {
          if (chatSocketService.user) {
            const socketUser = chatSocketService.user;
            console.log('🔥 Gecikmeli socket kullanıcı bilgisi:', socketUser);
            
            const gender = socketUser.gender?.toLowerCase();
            let defaultAvatar = "https://st3.depositphotos.com/6672868/13701/v/450/depositphotos_137014128-stock-illustration-user-profile-icon.jpg";
            if (gender === "male") defaultAvatar = "/assets/qriendManAvatar.png";
            else if (gender === "female")
              defaultAvatar = "/assets/qriendWomenAvatar.png";

            const userWithBackendId = {
              id: String(socketUser.id), // String'e çevir
              name: socketUser.name || socketUser.firstname || socketUser.email || "Kullanıcı",
              avatar: socketUser.avatar || socketUser.profilePicture || defaultAvatar,
              backendUserId: String(socketUser.backendUserId || socketUser.userId || socketUser.id),
            };

            console.log('👤 CurrentUser set edildi (socket):', userWithBackendId);
            setCurrentUser(userWithBackendId);
          }
        }, 1000);
      }
    } catch (error) {
      console.error("❌ Socket bağlantı hatası:", error);
      setSocketConnected(false);
      handleAuthError("Bağlantı kurulamadı");
    } finally {
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    const token = getToken("qriendtoken");
    if (!token) {
      setIsLoggedIn(false);
      return;
    }

    setIsLoggedIn(true);

    const fetchUserProfileAndCheckIn = async () => {
      try {
        console.log("🚀 User profile ve check-in başlatılıyor...");
        
        // Önce socket bağlantısını kur
        await connectToSocket();

        // API'den kullanıcı profilini de al (fallback için)
        const userProfile = await getUserProfile();
        console.log("👤 User profile alındı:", userProfile);

        if (!userProfile?.id && !chatSocketService.user) {
          handleAuthError("Kullanıcı profili yüklenemedi");
          return;
        }

        // Eğer socket'ten kullanıcı bilgisi gelmediyse API'den al
        if (!currentUser && userProfile?.id) {
          const gender = userProfile.gender?.toLowerCase();
          let defaultAvatar = "https://st3.depositphotos.com/6672868/13701/v/450/depositphotos_137014128-stock-illustration-user-profile-icon.jpg";
          if (gender === "male") defaultAvatar = "/assets/qriendManAvatar.png";
          else if (gender === "female")
            defaultAvatar = "/assets/qriendWomenAvatar.png";

          // DÜZELTME: Socket ID'yi kullan, backend user ID'yi değil
          const socketUserId = chatSocketService.user?.id || String(userProfile.id);

          const fallbackUser = {
            id: String(socketUserId), // Socket ID kullan
            name: userProfile.name || userProfile.firstname || "Kullanıcı",
            avatar:
              userProfile.avatar || userProfile.profilePicture || defaultAvatar,
            backendUserId: String(userProfile.id), // Backend ID'yi de sakla
          };

          setCurrentUser(fallbackUser);
          console.log("✅ Fallback user set (Socket ID kullanıldı):", fallbackUser);
        }

        if (venueUUID) {
          console.log("🏢 Venue check-in başlatılıyor:", venueUUID);
          await performCheckIn(venueUUID);
        }
      } catch (error) {
        console.error("❌ Kullanıcı profili API hatası:", error);
        handleAuthError("Kullanıcı doğrulama hatası");
      }
    };

    // Duplicate execution'ı önlemek için
    if (!isCheckingIn && !socketConnected) {
      fetchUserProfileAndCheckIn();
    }

    // Component unmount'ta socket'i temizle
    return () => {
      console.log("🧹 Component unmount, socket temizleniyor...");
      setSocketConnected(false);
      try {
        chatSocketService.leaveVenue();
      } catch (error) {
        console.warn("⚠️ Socket leave venue hatası (görmezden gelindi):", error);
      }
    };
  }, [venueUUID]); // Sadece venueUUID değiştiğinde çalışsın

  return {
    isLoggedIn,
    currentUser,
    setCurrentUser,
    isCheckingIn: isCheckingIn || isConnecting,
    checkInError,
    hasCheckedIn,
  };
};